<template>
  <div class="position-dialog-container">
    <teleport to="body">
      <!-- Elegant Floating Position Orb (matching your voice agent design) -->
      <div class="position-assistant-orb" @click="openPositioningDialog" :class="{ 'orb-visible': isVisible }">
        <v-tooltip location="left">
          <template #activator="{ props }">
            <div v-bind="props" class="position-orb-container" :class="{ 
              active: isPositioningActive,
              loading: isLoading,
              disabled: !isNativePlatform 
            }">
              <div class="position-orb-inner">
                <div ref="fabLottieRef" class="position-orb-avatar"></div>
              </div>
              
              <!-- Status Indicators -->
              <div v-if="isPositioningActive && positionData" class="position-status-indicators">
                <div class="accuracy-indicator" :class="accuracyStatus">
                  ±{{ positionData.accuracy?.toFixed(0) || '?' }}m
                </div>
                <div class="environment-indicator" :class="{ indoor: positionData?.isIndoor }">
                  <i :class="positionData?.isIndoor ? 'mdi mdi-home-variant' : 'mdi mdi-earth'"></i>
                </div>
              </div>
            </div>
          </template>
          <span>{{ fabTooltip }}</span>
        </v-tooltip>
      </div>
    </teleport>

    <!-- Modern Dialog with Compact Header -->
    <v-dialog
      v-model="isDialogOpen"
      fullscreen
      transition="dialog-bottom-transition"
      class="ultimate-dialog-wrapper"
      :scrim="true"
    >
      <v-card class="ultimate-dialog-card" elevation="24">
        <!-- Compact Enhanced Header -->
        <v-card-title class="dialog-header pa-0">
          <v-container fluid class="pa-4">
            <v-row align="center" no-gutters>
              <!-- Title and Status in One Row -->
              <v-col cols="auto" class="me-3">
                <v-avatar
                  color="white"
                  size="32"
                  class="elevation-4"
                >
                  <v-icon
                    icon="mdi-crosshairs-gps"
                    color="primary"
                    size="18"
                  />
                </v-avatar>
              </v-col>
              
              <v-col class="flex-grow-1">
                <h2 class="text-h6 font-weight-bold text-white mb-0">
                  Ultimate Positioning
                </h2>
                <p class="text-caption text-white text-opacity-80 mb-0">
                  {{ headerSubtitle }}
                </p>
              </v-col>

              <!-- Compact Status Chips -->
              <v-col v-if="isPositioningActive && positionData" cols="auto" class="me-3">
                <div class="status-chips d-flex ga-1">
                  <v-chip
                    :color="accuracyChipColor"
                    size="x-small"
                    class="elevation-2"
                  >
                    ±{{ positionData.accuracy?.toFixed(0) }}m
                  </v-chip>
                  <v-chip
                    :color="positionData?.isIndoor ? 'purple' : 'blue'"
                    size="x-small"
                    class="elevation-2"
                  >
                    <v-icon 
                      :icon="positionData?.isIndoor ? 'mdi-home-variant' : 'mdi-earth'"
                      size="12"
                      start
                    />
                    {{ positionData?.isIndoor ? 'Indoor' : 'Outdoor' }}
                  </v-chip>
                </div>
              </v-col>

              <!-- Close Button -->
              <v-col cols="auto">
                <v-btn
                  @click="closeDialog"
                  icon="mdi-close"
                  variant="text"
                  size="small"
                  class="close-button"
                  color="white"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-title>

        <!-- Map Content -->
        <v-card-text class="pa-0">
          <div class="dialog-content">
            <PositionMapView
              ref="mapViewRef"
              @position-update="handlePositionUpdate"
              @positioning-started="handlePositioningStarted"
              @positioning-stopped="handlePositioningStopped"
            />

            <!-- Start Overlay with Lottie Animation -->
            <v-fade-transition>
              <div v-if="!isPositioningActive && !isLoading" class="start-overlay">
                <v-container class="fill-height">
                  <v-row align="center" justify="center">
                    <v-col cols="12" sm="8" md="6" class="text-center">
                      <v-card class="start-card elevation-12" rounded="xl">
                        <v-card-text class="pa-8">
                          <!-- Lottie Animation -->
                          <div 
                            ref="startLottieRef" 
                            class="start-lottie mb-6"
                          />
                          
                          <h2 class="text-h4 font-weight-bold mb-4 text-primary">
                            Ultimate Positioning
                          </h2>
                          
                          <p class="text-h6 text-medium-emphasis mb-6">
                            Activate high-accuracy location tracking with GPS, WiFi, 
                            Bluetooth, and motion sensors for sub-meter precision
                          </p>
                          
                          <v-btn
                            @click="startPositioning"
                            :loading="isProcessing"
                            size="x-large"
                            rounded="pill"
                            class="start-btn elevation-4"
                            :style="buttonGradient"
                          >
                            <v-icon start icon="mdi-play" />
                            {{ isProcessing ? 'Starting...' : 'Start Positioning' }}
                          </v-btn>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-container>
              </div>
            </v-fade-transition>

            <!-- Loading Overlay with Lottie -->
            <v-fade-transition>
              <div v-if="isLoading" class="loading-overlay">
                <v-container class="fill-height">
                  <v-row align="center" justify="center">
                    <v-col cols="12" sm="8" md="6">
                      <v-card class="loading-card elevation-16" rounded="xl">
                        <v-card-text class="pa-8 text-center">
                          <!-- Lottie Loading Animation -->
                          <div 
                            ref="loadingLottieRef" 
                            class="loading-lottie mb-4"
                          />
                          
                          <h3 class="text-h5 font-weight-bold mb-3">
                            {{ loadingTitle }}
                          </h3>
                          <p class="text-body-1 text-medium-emphasis mb-4">
                            {{ loadingSubtitle }}
                          </p>
                          
                          <!-- Loading Progress -->
                          <v-progress-linear
                            :model-value="loadingProgress"
                            color="primary"
                            height="6"
                            rounded
                            class="mb-4"
                          />
                          
                          <!-- Loading Steps -->
                          <v-stepper
                            v-model="currentLoadingState"
                            alt-labels
                            class="elevation-0 bg-transparent"
                            hide-actions
                          >
                            <v-stepper-header>
                              <v-stepper-item
                                v-for="(step, index) in loadingSteps"
                                :key="index"
                                :complete="currentLoadingState > index + 1"
                                :value="index + 1"
                                :title="step.title"
                                :icon="step.icon"
                                color="primary"
                              />
                            </v-stepper-header>
                          </v-stepper>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-container>
              </div>
            </v-fade-transition>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, defineExpose, watch } from 'vue'
import { Capacitor } from '@capacitor/core'
import lottie from 'lottie-web'
import PositionMapView from './PositionMapView.vue'

// Import the Lottie animation JSON directly
import positionLottieData from '@/components/general/Positioning/lottie_position.json'

// Template refs
const mapViewRef = ref(null)
const fabLottieRef = ref(null)
const startLottieRef = ref(null)
const loadingLottieRef = ref(null)

// Lottie instances
let fabLottieInstance = null
let startLottieInstance = null
let loadingLottieInstance = null

// Reactive state
const isVisible = ref(false)
const isDialogOpen = ref(false)
const isPositioningActive = ref(false)
const isLoading = ref(false)
const isProcessing = ref(false)
const positionData = ref(null)
const isNativePlatform = ref(Capacitor.isNativePlatform())

// Loading states with icons
const loadingSteps = [
  { title: 'Initialize', icon: 'mdi-cog', subtitle: 'Setting up sensors...' },
  { title: 'Permissions', icon: 'mdi-shield-check', subtitle: 'Requesting access...' },
  { title: 'Acquire', icon: 'mdi-satellite-variant', subtitle: 'Scanning signals...' },
  { title: 'Process', icon: 'mdi-brain', subtitle: 'Processing location...' }
]

const currentLoadingState = ref(1)
const loadingProgress = ref(0)

// Theme colors and gradients
const primaryGradient = 'linear-gradient(135deg, #4fc3f7, #2196f3)'
const buttonGradient = `background: ${primaryGradient}; color: white;`

// Computed properties
const fabTooltip = computed(() => {
  if (!isNativePlatform.value) return 'Location only available on mobile'
  if (isLoading.value) return 'Acquiring position...'
  if (isPositioningActive.value) return `Position active - ±${positionData.value?.accuracy?.toFixed(0) || '?'}m`
  return 'Open Ultimate Location Map'
})

const headerSubtitle = computed(() => {
  if (isLoading.value) return 'Acquiring position...'
  if (isPositioningActive.value) return 'High-accuracy positioning active'
  return 'Ready to start positioning'
})

const accuracyStatus = computed(() => {
  if (!positionData.value?.accuracy) return 'unknown'
  const acc = positionData.value.accuracy
  if (acc <= 2) return 'perfect'
  if (acc <= 5) return 'excellent'
  if (acc <= 10) return 'good'
  if (acc <= 20) return 'fair'
  return 'poor'
})

const accuracyChipColor = computed(() => {
  switch (accuracyStatus.value) {
    case 'perfect': return 'success'
    case 'excellent': return 'info'
    case 'good': return 'primary'
    case 'fair': return 'warning'
    default: return 'error'
  }
})

const loadingTitle = computed(() => {
  return loadingSteps[currentLoadingState.value - 1]?.title || 'Loading...'
})

const loadingSubtitle = computed(() => {
  return loadingSteps[currentLoadingState.value - 1]?.subtitle || 'Please wait...'
})

// Lottie management for FAB
const initializeFabLottie = () => {
  if (fabLottieRef.value && !fabLottieInstance) {
    try {
      console.log('🎬 Initializing Position Orb Lottie animation...')
      
      fabLottieInstance = lottie.loadAnimation({
        container: fabLottieRef.value,
        renderer: 'svg',
        loop: true,
        autoplay: true, // Start playing immediately
        animationData: positionLottieData,
        rendererSettings: {
          preserveAspectRatio: 'xMidYMid meet'
        }
      })

      fabLottieInstance.addEventListener('DOMLoaded', () => {
        console.log('✅ Position Orb Lottie animation loaded successfully')
      })

      fabLottieInstance.addEventListener('error', (error) => {
        console.error('❌ Position Orb Lottie error:', error)
      })

    } catch (error) {
      console.error('❌ Failed to initialize Position Orb Lottie:', error)
    }
  }
}

const initializeLottie = () => {
  // Initialize start Lottie
  if (startLottieRef.value && !startLottieInstance) {
    try {
      startLottieInstance = lottie.loadAnimation({
        container: startLottieRef.value,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        animationData: positionLottieData,
        rendererSettings: {
          preserveAspectRatio: 'xMidYMid meet'
        }
      })
    } catch (error) {
      console.warn('Failed to load start Lottie:', error)
    }
  }
}

const initializeLoadingLottie = () => {
  if (loadingLottieRef.value && !loadingLottieInstance) {
    try {
      loadingLottieInstance = lottie.loadAnimation({
        container: loadingLottieRef.value,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        animationData: positionLottieData,
        rendererSettings: {
          preserveAspectRatio: 'xMidYMid meet'
        }
      })
    } catch (error) {
      console.warn('Failed to load loading Lottie:', error)
    }
  }
}

// Watch for loading state to initialize loading Lottie
watch(isLoading, (loading) => {
  if (loading) {
    nextTick(() => {
      initializeLoadingLottie()
    })
  } else {
    if (loadingLottieInstance) {
      loadingLottieInstance.destroy()
      loadingLottieInstance = null
    }
  }
})

// Methods
const openPositioningDialog = async () => {
  if (!isNativePlatform.value) {
    console.warn('Positioning only available on native platforms')
    return
  }

  console.log('🗺️ Opening positioning dialog...')
  isDialogOpen.value = true

  // Initialize Lottie animations after dialog opens
  await nextTick()
  initializeLottie()

  // Auto-start positioning if not active
  if (!isPositioningActive.value) {
    await nextTick()
    startPositioning()
  }
}

const closeDialog = async () => {
  console.log('❌ Closing positioning dialog...')

  try {
    // Stop positioning through map view
    if (mapViewRef.value && isPositioningActive.value) {
      console.log('🛑 Stopping positioning through map view...')
      await mapViewRef.value.forceStopPositioning()
    }

    // Close dialog
    isDialogOpen.value = false

    // Reset states
    resetDialogState()

    // Clean up Lottie instances
    cleanupLottie()

    console.log('✅ Dialog closed successfully')

  } catch (error) {
    console.error('❌ Error closing dialog:', error)
    // Force close
    isDialogOpen.value = false
    resetDialogState()
    cleanupLottie()
  }
}

const startPositioning = async () => {
  if (isProcessing.value || !mapViewRef.value) return

  try {
    isProcessing.value = true
    isLoading.value = true
    currentLoadingState.value = 1
    loadingProgress.value = 0

    // Animate progress through loading states
    const progressInterval = setInterval(() => {
      loadingProgress.value = Math.min(loadingProgress.value + 2, 95)
    }, 100)

    // Cycle through loading states
    const stateInterval = setInterval(() => {
      if (currentLoadingState.value < loadingSteps.length) {
        currentLoadingState.value++
        loadingProgress.value = (currentLoadingState.value / loadingSteps.length) * 80
      } else {
        clearInterval(stateInterval)
        clearInterval(progressInterval)
      }
    }, 1500)

    // Start positioning through map view
    await mapViewRef.value.startPositioning()

    clearInterval(stateInterval)
    clearInterval(progressInterval)
    loadingProgress.value = 100

  } catch (error) {
    console.error('❌ Failed to start positioning:', error)
    isLoading.value = false
    currentLoadingState.value = 1
    loadingProgress.value = 0
  } finally {
    isProcessing.value = false
  }
}

const resetDialogState = () => {
  isPositioningActive.value = false
  isLoading.value = false
  isProcessing.value = false
  positionData.value = null
  currentLoadingState.value = 1
  loadingProgress.value = 0
}

const cleanupLottie = () => {
  if (startLottieInstance) {
    startLottieInstance.destroy()
    startLottieInstance = null
  }
  if (loadingLottieInstance) {
    loadingLottieInstance.destroy()
    loadingLottieInstance = null
  }
}

// Event handlers
const handlePositionUpdate = (data) => {
  positionData.value = data

  // Stop loading when position is received
  if (isLoading.value) {
    loadingProgress.value = 100
    setTimeout(() => {
      isLoading.value = false
    }, 500)
  }

  console.log('📍 Position updated in dialog:', data)
}

const handlePositioningStarted = () => {
  isPositioningActive.value = true
  console.log('✅ Positioning started in dialog')
}

const handlePositioningStopped = () => {
  resetDialogState()
  console.log('🛑 Positioning stopped in dialog')
}

// Keyboard handling
const handleKeydown = (event) => {
  if (event.key === 'Escape' && isDialogOpen.value) {
    closeDialog()
  }
}

// Lifecycle
onMounted(() => {
  console.log('🚀 PositionDialog mounted')

  // Show orb with delay
  setTimeout(() => {
    isVisible.value = true
    // Initialize FAB Lottie after it's visible
    nextTick(() => {
      initializeFabLottie()
    })
  }, 500)

  // Add event listeners
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(async () => {
  console.log('🧹 PositionDialog unmounting...')

  // Clean up event listeners
  document.removeEventListener('keydown', handleKeydown)

  // Clean up Lottie instances
  if (fabLottieInstance) {
    fabLottieInstance.destroy()
    fabLottieInstance = null
  }
  cleanupLottie()

  // Force stop positioning if needed
  if (mapViewRef.value && isPositioningActive.value) {
    await mapViewRef.value.forceStopPositioning()
  }
})

// Expose methods for parent components
defineExpose({
  openDialog: openPositioningDialog,
  closeDialog
})
</script>

<style scoped>
.position-dialog-container {
  position: relative;
  z-index: 1000;
}

/* Position Orb Container - Based on Voice Agent Design */
.position-assistant-orb {
  position: fixed;
  bottom: 170px;
  right: 50px;
  z-index: 1000;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  transform: scale(0) rotate(-180deg);
  opacity: 0;
}

.position-assistant-orb.orb-visible {
  transform: scale(1) rotate(0deg);
  opacity: 1;
}

.position-orb-container {
  position: relative;
  width: 96px;
  height: 96px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.position-orb-container.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.position-orb-container:not(.disabled):hover {
  transform: scale(1.1);
}

/* Orb Inner Container - Clean transparent container */
.position-orb-inner {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: transparent;
}

/* Lottie Animation Container - Now fills entire orb */
.position-orb-avatar {
  width: 92px;
  height: 92px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;
  border-radius: 50%;
  overflow: hidden;
}

.position-orb-avatar svg {
  width: 100% !important;
  height: 100% !important;
  border-radius: 50%;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.3));
}

/* Status Indicators */
.position-status-indicators {
  position: absolute;
  top: -12px;
  right: -12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-end;
  z-index: 10;
}

.accuracy-indicator {
  background: rgba(255, 255, 255, 0.95);
  color: #333;
  border-radius: 12px;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: bold;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  border: 2px solid;
}

.accuracy-indicator.perfect { border-color: #4caf50; color: #2e7d32; }
.accuracy-indicator.excellent { border-color: #2196f3; color: #1565c0; }
.accuracy-indicator.good { border-color: #ff9800; color: #ef6c00; }
.accuracy-indicator.fair { border-color: #ff5722; color: #d84315; }
.accuracy-indicator.poor { border-color: #f44336; color: #c62828; }

.environment-indicator {
  width: 24px;
  height: 24px;
  background: rgba(33, 150, 243, 0.9);
  border: 2px solid white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.environment-indicator.indoor {
  background: rgba(156, 39, 176, 0.9);
}

/* Dialog Styling */
.ultimate-dialog-wrapper .v-overlay__content {
  margin: 0 !important;
  height: 100vh !important;
  width: 100vw !important;
}

.ultimate-dialog-card {
  height: 100vh;
  border-radius: 0 !important;
}

/* Compact Dialog Header */
.dialog-header {
  background: linear-gradient(135deg, #4fc3f7, #2196f3);
  color: white;
  position: relative;
  overflow: hidden;
  min-height: 64px;
}

.dialog-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 50%, rgba(255, 255, 255, 0.05) 100%);
  pointer-events: none;
}

.status-chips {
  flex-wrap: wrap;
}

.close-button {
  background: rgba(0, 0, 0, 0.15) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.close-button:hover {
  background: rgba(0, 0, 0, 0.25) !important;
  transform: scale(1.05);
}

/* Dialog Content */
.dialog-content {
  position: relative;
  height: calc(100vh - 64px);
  background: #f5f5f5;
}

/* Start Overlay */
.start-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(245, 245, 245, 0.98);
  backdrop-filter: blur(10px);
  z-index: 1000;
}

.start-card {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px);
}

.start-lottie {
  width: 120px;
  height: 120px;
  margin: 0 auto;
}

.start-btn {
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: none;
  min-height: 56px;
}

.start-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(79, 195, 247, 0.4) !important;
}

/* Loading Overlay */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(245, 245, 245, 0.98);
  backdrop-filter: blur(10px);
  z-index: 1000;
}

.loading-card {
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(79, 195, 247, 0.2);
}

.loading-lottie {
  width: 100px;
  height: 100px;
  margin: 0 auto;
}

/* Responsive Design */
@media (max-width: 960px) {
  .position-assistant-orb {
    bottom: 140pxl;
    right: 10px;
  }
  
  .dialog-content {
    height: calc(100vh - 64px);
  }
}

@media (max-width: 600px) {
  .position-assistant-orb {
     bottom: 140px;
        right: 15px;
  }
  
  .position-orb-container {
    width: 76px;
    height: 76px;
  }
  
  .position-orb-avatar {
    width: 72px;
    height: 72px;
  }
  
  .position-status-indicators {
    top: -8px;
    right: -8px;
    gap: 2px;
  }
  
  .accuracy-indicator {
    font-size: 10px;
    padding: 2px 8px;
  }
  
  .environment-indicator {
    width: 20px;
    height: 20px;
    font-size: 10px;
  }
  
  .dialog-header {
    padding: 8px !important;
    min-height: 56px;
  }
  
  .status-chips {
    display: none !important;
  }
  
  .dialog-content {
    height: calc(100vh - 56px);
  }
  
  .start-lottie {
    width: 100px;
    height: 100px;
  }
  
  .loading-lottie {
    width: 80px;
    height: 80px;
  }
}

@media (max-width: 400px) {
  .position-assistant-orb {
    bottom: 130px;
    right: 12px;
  }
  
  .position-orb-container {
    width: 72px;
    height: 72px;
  }
  
  .position-orb-avatar {
    width: 68px;
    height: 68px;
  }
  
  .start-lottie {
    width: 80px;
    height: 80px;
  }
  
  .loading-lottie {
    width: 60px;
    height: 60px;
  }
}
</style>